import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { UserRow } from '../../../core/models/api.models';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [DatePipe, RouterLink, FormsModule],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
})
export class UserDetailComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly user = signal<UserRow | null>(null);
  readonly showReject = signal(false);
  readonly creditWalletId = signal<number | null>(null);
  readonly creditKobbleWalletId = signal<string | null>(null);
  rejectionReason = '';
  creditAmount = 1000;
  creditReference = 'Admin topup';
  pointsAmount = 100;
  pointsDirection: 'credit' | 'debit' = 'credit';
  pointsReason = '';
  statementDateFrom = '';
  statementDateTo = '';
  statementFormat: 'pdf' | 'csv' = 'pdf';
  readonly emailingStatement = signal(false);
  readonly unlockingLogin = signal(false);

  ngOnInit(): void {
    this.initStatementDates();
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set('Missing user id');
      this.loading.set(false);
      return;
    }
    this.load(id);
  }

  load(id: string | number): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getUserById(id).subscribe({
      next: (user) => {
        this.user.set(user);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load user');
        this.loading.set(false);
      },
    });
  }

  fullName(user: UserRow): string {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
  }

  isLoginLocked(user: UserRow): boolean {
    return Boolean(user.is_locked || user.is_permanently_locked || user.is_temporarily_locked);
  }

  unlockLogin(): void {
    const user = this.user();
    if (!user || this.unlockingLogin() || this.saving()) return;
    if (!this.isLoginLocked(user)) {
      this.error.set('This user is not locked');
      return;
    }
    if (!confirm('Unblock this user so they can sign in again?')) return;

    this.unlockingLogin.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.unlockUserLogin(user.id).subscribe({
      next: () => {
        this.unlockingLogin.set(false);
        this.success.set('User unblocked. They can sign in again.');
        this.load(user.id);
      },
      error: (err: { message?: string }) => {
        this.unlockingLogin.set(false);
        this.error.set(err?.message || 'Failed to unblock user');
      },
    });
  }

  approve(): void {
    const user = this.user();
    if (!user || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.updateKycStatus({ user_id: user.id, kyc_status: 'approved' }).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('KYC approved');
        this.load(user.id);
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to approve KYC');
      },
    });
  }

  openReject(): void {
    this.showReject.set(true);
    this.rejectionReason = '';
  }

  cancelReject(): void {
    this.showReject.set(false);
    this.rejectionReason = '';
  }

  confirmReject(): void {
    const user = this.user();
    const reason = this.rejectionReason.trim();
    if (!user || !reason || this.saving()) return;

    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api
      .updateKycStatus({
        user_id: user.id,
        kyc_status: 'rejected',
        rejection_reason: reason,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.showReject.set(false);
          this.success.set('KYC rejected');
          this.load(user.id);
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Failed to reject KYC');
        },
      });
  }

  openCredit(walletId: number, kobbleWalletId?: string | null): void {
    this.creditWalletId.set(walletId);
    this.creditKobbleWalletId.set(kobbleWalletId || null);
    this.creditAmount = 1000;
    this.creditReference = 'Admin topup';
  }

  cancelCredit(): void {
    this.creditWalletId.set(null);
    this.creditKobbleWalletId.set(null);
  }

  confirmCredit(): void {
    const walletId = this.creditWalletId();
    const kobbleWalletId = this.creditKobbleWalletId();
    const user = this.user();
    if (!walletId || !user || this.saving() || this.creditAmount <= 0) return;

    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api
      .addManualCredit({
        wallet_id: walletId,
        kobble_wallet_id: kobbleWalletId || undefined,
        amount: Number(this.creditAmount),
        reference: this.creditReference || undefined,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.creditWalletId.set(null);
          this.creditKobbleWalletId.set(null);
          this.success.set(`Manual credit added to wallet #${walletId}`);
          this.load(user.id);
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Failed to add manual credit');
        },
      });
  }

  confirmPointsAdjust(): void {
    const user = this.user();
    const amount = Math.abs(parseInt(String(this.pointsAmount || 0), 10) || 0);
    const reason = this.pointsReason.trim();
    if (!user || this.saving() || amount < 1 || !reason) return;
    const points = this.pointsDirection === 'debit' ? -amount : amount;

    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.adjustUserPoints({ user_id: user.id, points, reason }).subscribe({
      next: (data) => {
        this.saving.set(false);
        this.pointsReason = '';
        const verb = points > 0 ? 'credited' : 'debited';
        this.success.set(
          `${amount} points ${verb}. Balance now ${data.points_balance}.`
        );
        this.load(user.id);
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to adjust points');
      },
    });
  }
  private initStatementDates(): void {
    const to = new Date();
    const from = new Date(to);
    // Inclusive window of 366 days (to and the prior 365 days)
    from.setDate(from.getDate() - 365);
    this.statementDateTo = this.toDateInput(to);
    this.statementDateFrom = this.toDateInput(from);
  }

  private toDateInput(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private statementRangeValid(): boolean {
    const from = this.statementDateFrom?.trim();
    const to = this.statementDateTo?.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      return false;
    }
    const fromMs = Date.parse(from + 'T00:00:00Z');
    const toMs = Date.parse(to + 'T00:00:00Z');
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) {
      return false;
    }
    const inclusiveDays = Math.floor((toMs - fromMs) / 86400000) + 1;
    return inclusiveDays <= 366;
  }

  confirmEmailStatement(): void {
    const user = this.user();
    if (!user || this.emailingStatement() || this.saving()) return;
    if (!user.email) {
      this.error.set('User has no email on file');
      return;
    }
    if (!this.statementRangeValid()) {
      this.error.set('Choose a valid date range of at most 366 days');
      return;
    }

    this.emailingStatement.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api
      .emailUserTransactionStatement({
        user_id: user.id,
        date_from: this.statementDateFrom.trim(),
        date_to: this.statementDateTo.trim(),
        format: this.statementFormat,
      })
      .subscribe({
        next: (data) => {
          this.emailingStatement.set(false);
          this.success.set(
            `Statement emailed to ${data.email} (${data.row_count} transactions, ${data.format.toUpperCase()})`
          );
        },
        error: (err: { message?: string }) => {
          this.emailingStatement.set(false);
          this.error.set(err?.message || 'Failed to email statement');
        },
      });
  }
}
